import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { patientsApi, GetPagedPatientsParams } from '../api/patients.api';
import { CreatePatientDto, UpdatePatientDto } from '../types/patient.types';

export const usePatients = () => {
  return useQuery({
    queryKey: ['patients'],
    queryFn: () => patientsApi.getAll(),
    staleTime: 5 * 60 * 1000, // 5 minutes - data is fresh for 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes - cache for 10 minutes
  });
};

export const usePatient = (id: number) => {
  return useQuery({
    queryKey: ['patients', id],
    queryFn: () => patientsApi.getById(id),
    enabled: !!id,
  });
};

export const useSearchPatients = (query: string) => {
  return useQuery({
    queryKey: ['patients', 'search', query],
    queryFn: () => patientsApi.search(query),
    enabled: query.length > 0,
    staleTime: 30000, // Cache for 30 seconds
  });
};

export const usePagedPatients = (params: GetPagedPatientsParams) => {
  return useQuery({
    queryKey: ['patients', 'paged', params],
    queryFn: () => patientsApi.getPaged(params),
    staleTime: 30000, // Cache for 30 seconds
    retry: 2, // Retry failed requests twice
  });
};

export const useCreatePatient = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreatePatientDto) => patientsApi.create(data),
    onSuccess: () => {
      // Invalidate all patient-related queries
      queryClient.invalidateQueries({ queryKey: ['patients'] });
    },
  });
};

export const useUpdatePatient = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdatePatientDto }) =>
      patientsApi.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['patients'] });
      queryClient.invalidateQueries({ queryKey: ['patients', variables.id] });
    },
  });
};

export const useDeletePatient = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => patientsApi.delete(id),
    onSuccess: () => {
      // Invalidate all patient-related queries
      queryClient.invalidateQueries({ queryKey: ['patients'] });
    },
  });
};

